import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "@/components/Logo";
import { useSiteSettings } from "@/lib/settings";
import { Mail, MessageCircle, Instagram, Facebook, Globe, Phone, MapPin, Clock } from "lucide-react";

const PLATFORM_ICONS = {
  instagram: Instagram, facebook: Facebook, whatsapp: MessageCircle,
  tiktok: Globe, xhs: Globe, wechat: Globe, other: Globe,
};

const PLATFORM_HOVER = {
  instagram: "hover:text-[#E1306C]", facebook: "hover:text-[#1877F2]",
  whatsapp: "hover:text-[#25D366]", tiktok: "hover:text-white",
  xhs: "hover:text-[#FF2442]", wechat: "hover:text-[#07C160]",
  other: "hover:text-[#00a8a8]",
};

export default function Footer() {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const settings = useSiteSettings();
  const socials = settings.social_links || [];
  const address = zh ? settings.contact_address_zh : settings.contact_address_en;
  const hours = zh ? settings.business_hours_zh : settings.business_hours_en;
  const company = settings.company_name || "CiAO Ideology";

  return (
    <footer className="relative mt-24 border-t border-[#e5ebeb] bg-[#003645] text-white" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid gap-12 md:grid-cols-3">
        <div>
          <Logo size="md" light />
          <p className="mt-6 text-sm text-white/70 font-serif-cn leading-relaxed max-w-xs">
            {t("brand.tagline")} — {t("brand.subtitle")}
          </p>
          {socials.length > 0 && (
            <div className="mt-6 flex items-center gap-4" data-testid="footer-social">
              {socials.map((s, i) => {
                const Icon = PLATFORM_ICONS[s.platform] || Globe;
                const hover = PLATFORM_HOVER[s.platform] || "hover:text-[#00a8a8]";
                return (
                  <a
                    key={i}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label || s.platform}
                    className={`text-white/70 transition-colors duration-200 ${hover}`}
                    data-testid={`footer-social-${s.platform}`}
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-white/50 font-semibold">
            {t("footer.quickLinks")}
          </div>
          <ul className="mt-4 space-y-2 text-sm text-white/80">
            <li><Link to="/about" className="hover:text-[#00a8a8] transition-colors duration-200">{t("nav.about")}</Link></li>
            <li><Link to="/products" className="hover:text-[#00a8a8] transition-colors duration-200">{t("nav.products")}</Link></li>
            <li><Link to="/crystals" className="hover:text-[#00a8a8] transition-colors duration-200">{t("nav.crystals")}</Link></li>
            <li><Link to="/booking" className="hover:text-[#00a8a8] transition-colors duration-200">{t("nav.booking")}</Link></li>
            <li><Link to="/dashboard" className="hover:text-[#00a8a8] transition-colors duration-200">{t("nav.member")}</Link></li>
          </ul>
        </div>

        <div data-testid="footer-contact">
          <div className="text-xs uppercase tracking-widest text-white/50 font-semibold">
            {t("footer.contact")}
          </div>
          <ul className="mt-4 space-y-2 text-sm text-white/80">
            {settings.contact_phone && (
              <li className="flex items-center gap-2"><Phone size={14} className="text-[#00a8a8]" /> {settings.contact_phone}</li>
            )}
            {settings.whatsapp_number && (
              <li className="flex items-center gap-2"><MessageCircle size={14} className="text-[#00a8a8]" /> {settings.whatsapp_number}</li>
            )}
            {settings.site_email && (
              <li className="flex items-center gap-2"><Mail size={14} className="text-[#00a8a8]" /> {settings.site_email}</li>
            )}
            {address && (
              <li className="flex items-start gap-2"><MapPin size={14} className="text-[#00a8a8] mt-0.5 shrink-0" /> <span>{address}</span></li>
            )}
            {hours && (
              <li className="flex items-center gap-2"><Clock size={14} className="text-[#00a8a8]" /> {hours}</li>
            )}
            {settings.bank_name && (
              <li className="text-white/60 text-xs mt-3 leading-relaxed">
                {settings.bank_name} · {settings.bank_account_name}
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-white/50">
          <span data-testid="footer-copyright">© {new Date().getFullYear()} {company} · 晶灵文创. {t("footer.rights")}.</span>
          <span className="font-serif-cn">{t("footer.tagline")}</span>
        </div>
      </div>
    </footer>
  );
}
